//session, local storage 데이터 함수 처리

import {
  HAS_CLOSED_PROFILE_FILL_REQUEST_KEY,
  HAS_CLOSED_STARTUP_CHATTING_GUIDE_KEY,
  HAS_CLOSED_USER_CHATTING_GUIDE_KEY,
  HIDE_APP_INSTALL_PROMPT_POP_UP,
  HIDE_NATURAL_LANGUAGE_BETA_NOTICE_KEY,
  HIDE_PENALTY_NOTICE_STORAGE_KEY,
  HIDE_SCOUT_PAGE_NOTICE,
  HIDE_STARTUP_DETAIL_FORM_GUIDE_KEY,
  HIDE_STARTUP_POSITION_FORM_GUIDE_KEY,
  HIDE_PROFILE_FILL_NEED_MODAL_KEY,
  SKIP_POSITION_APPLICATION_PREVIEW_KEY,
  IS_HIDE_RECOMMENDED_GUIDE_BOX_KEY,
  AI_ASSESSMENT_NOTICE_POP_UP_KEY,
  AI_ASSESSMENT_CONDITIONS_KEY,
  COFFEE_CHAT_NOTICE_STORAGE_KEY,
  PROPOSAL_AUTO_CHECKED_FULFILLMENTS_KEY,
  DASHBOARD_RECOMMENDED_GUIDE_BOX_KEY,
  ZIGHANG_USER_STORAGE_KEY,
  POSITION_MY_MENU_POPUP_STORAGE_KEY,
  MY_PROFILE_ANALYSIS_FEEDBACK_STORAGE_KEY,
  IS_BROWSER,
  HIDE_RECEIVING_NEW_FIT_STARTUP_ALERT_KEY,
  HIDE_PORTFOLIO_SAMPLE_CTA_KEY,
  PRE_USER_INFO_KEY,
  FUNNEL_KEY,
  VERIFICATION_FORM_KEY,
  SALARY_ANALYSIS_FORM_KEY,
  HIDE_NO_TECH_STACK_MODAL_KEY,
} from 'infra/constants'
import {
  HidePenaltyNoticeType,
  MyProfileAnalysisFeedback,
  PositionType,
  TechStack,
} from 'types/common'

interface Expiration {
  expiresAt: number
}

type EnvelopeStorage<T> = T | null

export const getLocalStorage = <T>(key: string, fallback: T): T => {
  if (!IS_BROWSER) return fallback
  try {
    const row = localStorage.getItem(key)
    if (!row) return fallback
    return JSON.parse(row) as T
  } catch {
    return fallback
  }
}

export const setLocalStorage = <T>(key: string, value: T): void => {
  localStorage.setItem(key, JSON.stringify(value))
}

export const getClosedProfileFillRequestStorage = () =>
  JSON.parse(
    localStorage.getItem(HAS_CLOSED_PROFILE_FILL_REQUEST_KEY) as string,
  ) as EnvelopeStorage<number[]>

export const getHidePenaltyNoticeStorage = () =>
  JSON.parse(
    localStorage.getItem(HIDE_PENALTY_NOTICE_STORAGE_KEY) as string,
  ) as EnvelopeStorage<{
    type: HidePenaltyNoticeType
    expire: number
  }>

export const getUserChattingGuideStorage = () =>
  JSON.parse(
    localStorage.getItem(HAS_CLOSED_USER_CHATTING_GUIDE_KEY) as string,
  ) as EnvelopeStorage<boolean>

export const getStartupChattingGuideStorage = () =>
  JSON.parse(
    localStorage.getItem(HAS_CLOSED_STARTUP_CHATTING_GUIDE_KEY) as string,
  ) as EnvelopeStorage<boolean>

export const getStartupDetailFormGuideStorage = () =>
  JSON.parse(
    localStorage.getItem(HIDE_STARTUP_DETAIL_FORM_GUIDE_KEY) as string,
  ) as EnvelopeStorage<boolean>

export const getStartupPositionFormGuideStorage = () =>
  JSON.parse(
    localStorage.getItem(HIDE_STARTUP_POSITION_FORM_GUIDE_KEY) as string,
  ) as EnvelopeStorage<{ expire: number }>

export const getNaturalLanguageInputBetaNoticeStorage = () =>
  JSON.parse(
    localStorage.getItem(HIDE_NATURAL_LANGUAGE_BETA_NOTICE_KEY) as string,
  ) as EnvelopeStorage<boolean>

export const getScoutPageNoticeStore = () =>
  JSON.parse(localStorage.getItem(HIDE_SCOUT_PAGE_NOTICE) as string)

export const getProfileFillNeedModalStorage = () =>
  JSON.parse(
    localStorage.getItem(HIDE_PROFILE_FILL_NEED_MODAL_KEY) as string,
  ) as EnvelopeStorage<boolean>

export const getAppInstallPromptPopUpStorage = () =>
  JSON.parse(
    localStorage.getItem(HIDE_APP_INSTALL_PROMPT_POP_UP) as string,
  ) as EnvelopeStorage<boolean>

export const getPositionApplicationPreviewSkipStorage = () =>
  JSON.parse(
    localStorage.getItem(SKIP_POSITION_APPLICATION_PREVIEW_KEY) as string,
  ) as EnvelopeStorage<{ expire: number }>

export const getIsHideRecommendedGuideBoxStorage = () =>
  JSON.parse(
    localStorage.getItem(IS_HIDE_RECOMMENDED_GUIDE_BOX_KEY) as string,
  ) as EnvelopeStorage<boolean>

export const getMarketingContentSharedStorage = (storageKey: string) =>
  JSON.parse(localStorage.getItem(storageKey) as string) as EnvelopeStorage<{
    id: string
    isShared: boolean
  }>

export const getAiAssessmentNoticePopUpStorage = () =>
  JSON.parse(
    localStorage.getItem(AI_ASSESSMENT_NOTICE_POP_UP_KEY) as string,
  ) as EnvelopeStorage<{ expire: number }>

export const getAiAssessmentNoticeConditionStorage = (id: Number) =>
  JSON.parse(
    localStorage.getItem(AI_ASSESSMENT_CONDITIONS_KEY + `_${id}`) as string,
  ) as EnvelopeStorage<string[]>

export const getCoffeeChatNoticeStorage = () =>
  JSON.parse(
    localStorage.getItem(COFFEE_CHAT_NOTICE_STORAGE_KEY) as string,
  ) as EnvelopeStorage<boolean>

export const getProposalAutoCheckedFulfillments = (id: Number) =>
  JSON.parse(
    localStorage.getItem(
      PROPOSAL_AUTO_CHECKED_FULFILLMENTS_KEY + `_${id}`,
    ) as string,
  ) as EnvelopeStorage<string[]>

export const getRecommendedGuideBox = () =>
  JSON.parse(
    localStorage.getItem(DASHBOARD_RECOMMENDED_GUIDE_BOX_KEY) as string,
  ) as EnvelopeStorage<boolean>

export const getZighangUserStorage = () =>
  JSON.parse(
    localStorage.getItem(ZIGHANG_USER_STORAGE_KEY) as string,
  ) as EnvelopeStorage<{
    client_id: string
    utm_medium: string
    utm_source: string
    zighang_source_url: string
    careerTypes?: string[] | string
    positionTypes?: string[] | string
    serviceAreas?: string[] | string
    locations?: string[] | string
  }>

export const getPositionMyMenuPopupStorage = () =>
  JSON.parse(
    localStorage.getItem(POSITION_MY_MENU_POPUP_STORAGE_KEY) as string,
  ) as EnvelopeStorage<{
    isHiddenMyProfileAnalysis: boolean
    isHiddenProfilePublished: boolean
  }>

export const getMyProfileAnalysisFeedbackStorage = () => {
  return getLocalStorage<MyProfileAnalysisFeedback>(
    MY_PROFILE_ANALYSIS_FEEDBACK_STORAGE_KEY,
    {
      expired: null,
      ignoredCount: 0,
      isCompletedFeedback: false,
      isCompletedReanalysisRequested: false,
    },
  )
}

export const setMyProfileAnalysisFeedbackStorage = (
  value: Partial<MyProfileAnalysisFeedback>,
) => {
  const storage = getMyProfileAnalysisFeedbackStorage()
  setLocalStorage<MyProfileAnalysisFeedback>(
    MY_PROFILE_ANALYSIS_FEEDBACK_STORAGE_KEY,
    { ...storage, ...value },
  )
}

export const getHideReceivingNewFitStartupAlertStorage = () => {
  return getLocalStorage<Expiration>(HIDE_RECEIVING_NEW_FIT_STARTUP_ALERT_KEY, {
    expiresAt: 0,
  })
}

export const setHideReceivingNewFitStartupAlertStorage = (
  value: Expiration,
) => {
  setLocalStorage<Expiration>(HIDE_RECEIVING_NEW_FIT_STARTUP_ALERT_KEY, value)
}

export const getHidePortfolioSampleStorage = () => {
  return getLocalStorage<boolean>(HIDE_PORTFOLIO_SAMPLE_CTA_KEY, false)
}

export const getFunnelStorage = () => {
  return getLocalStorage<string | null>(FUNNEL_KEY, null)
}

export const getPreUserInfoStorage = () => {
  return getLocalStorage<{
    experience: number
    interestedPositionTypes: PositionType[]
    interestedTechStacks: TechStack[]
  } | null>(PRE_USER_INFO_KEY, null)
}

export const getVerificationFormStorage = () => {
  return getLocalStorage<{
    name: string
    phone: string
    birth: string
    isChecked: boolean
    isLoading: boolean
  } | null>(VERIFICATION_FORM_KEY, null)
}

export const getSalaryAnalysisFormStorage = () => {
  return getLocalStorage<{
    position: PositionType | null
    experience: number
    salary: number
    isVerificationNotRequired: boolean
  } | null>(SALARY_ANALYSIS_FORM_KEY, null)
}

export const getHideNoTechStackModalStorage = () => {
  return getLocalStorage<Expiration>(HIDE_NO_TECH_STACK_MODAL_KEY, {
    expiresAt: 0,
  })
}

export const setHideNoTechStackModalStorage = (value: Expiration) => {
  setLocalStorage<Expiration>(HIDE_NO_TECH_STACK_MODAL_KEY, value)
}
